import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    FormHelperText,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'sonner';
import { routingService } from '../../services/RoutingService';
import { useEffect } from 'react';
import TodoListInput from './TodoListInput';

interface FormValues {
    name: string;
    fallback_model: string;
    utterance: [];
    instance_id: string;
}

const validationSchema = Yup.object({
    name: Yup.string().required('Target model is required'),
    fallback_model: Yup.string()
        .required('Fallback model is required')
        .test('different-from-target', 'Fallback model must be different from target model', function (value) {
            return value !== this.parent.name;
        }),
    utterance: Yup.array()
        .of(Yup.string().trim())
        .min(1, "At least one utterance is required")
        .required("At least one utterance is required"),
});


export function RoutingFormComponent({ formValue, currentDeploymentId, models, deploymentInstance, routingDetail }: { formValue: any, currentDeploymentId: string, models: any[], deploymentInstance: any, routingDetail: any }) {
    const formik = useFormik<FormValues>({
        initialValues: formValue,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            try {
                await routingService.addRoute(values);
                toast.success('Routing rule created successfully');
            } catch (error) {
                toast.error('Failed to create routing rule');
            }
        },
    });

    useEffect(() => {
        if (routingDetail) {
            formik.setFieldValue('name', deploymentInstance.model_name);
            formik.setFieldValue('fallback_model', routingDetail.fallback_model || '');
            formik.setFieldValue('utterance', routingDetail.utterance || []);
        }
    }, [routingDetail, deploymentInstance]);

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Stack spacing={4}>
                    {/* Sample Utterances Section */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
                            Utterances <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Add sample prompts or queries that should trigger this routing rule
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TodoListInput
                                    name="utterance"
                                    value={formik.values.utterance || []}
                                    onChange={(newList) => formik.setFieldValue('utterance', newList)}
                                    error={formik.touched.utterance && Boolean(formik.errors.utterance)}
                                />
                            </Box>
                            {formik.touched.utterance && formik.errors.utterance && (
                                <FormHelperText error sx={{ ml: 2 }}>
                                    {formik.errors.utterance}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Box>

                    {/* Model Configuration Section */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 3 }}>
                            Model Configuration
                        </Typography>
                        <Stack spacing={3}>
                            <FormControl
                                fullWidth
                                error={formik.touched.fallback_model && Boolean(formik.errors.fallback_model)}
                            >
                                <InputLabel>Fallback Model</InputLabel>
                                <Select
                                    name="fallback_model"
                                    value={formik.values.fallback_model || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    label="Fallback Model"
                                >
                                    {models
                                        .filter(d => d.model_name !== formik.values.name)
                                        .map((deployment) => (
                                            <MenuItem key={deployment.id} value={deployment.model_name}>
                                                {deployment.modelName} ({deployment.name})
                                            </MenuItem>
                                        ))}
                                </Select>
                                {formik.touched.fallback_model && formik.errors.fallback_model && (
                                    <FormHelperText>{formik.errors.fallback_model}</FormHelperText>
                                )}
                            </FormControl>
                        </Stack>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => formik.resetForm()}
                            disabled={!formik.dirty}
                            sx={{ px: 3 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={formik.isSubmitting}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontWeight: 700,
                                px: 4,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                                },
                                '&:disabled': {
                                    background: '#cbd5e1',
                                    color: '#94a3b8'
                                },
                                transition: 'all 0.3s'
                            }}
                        >
                            {formik.isSubmitting ? 'Creating...' : 'Create Routing Rule'}
                        </Button>
                    </Box>
                </Stack>
            </form>
        </Box>
    );
}

export default RoutingFormComponent;
